/**
 * Tenant file-exchange configuration (TO-391, Epic 25 F1).
 *
 * Each tenant has exactly the five {@link LogicalFileType}s. A file says
 * whether it is enabled, which import definition reads it, how it may arrive
 * and when it is expected. F2–F5 read this shape; they never change it.
 * No credential is ever part of it — an SFTP method names its connector only.
 */

/** The five logical files, in their fixed listing order. */
export enum LogicalFileType {
	ORGANIZATIONAL = 'organizational',
	CLIENTS = 'clients',
	CONVERSIONS = 'conversions',
	USERS = 'users',
	PRODUCTS = 'products',
}

/** The fixed order every listing uses (spec REQ-001). */
export const LOGICAL_FILE_TYPES: readonly LogicalFileType[] = [
	LogicalFileType.ORGANIZATIONAL,
	LogicalFileType.CLIENTS,
	LogicalFileType.CONVERSIONS,
	LogicalFileType.USERS,
	LogicalFileType.PRODUCTS,
]

/** How a file may arrive — the discriminator of {@link RetrievalMethod}. */
export enum RetrievalMethodType {
	SFTP_PULL = 'sftp-pull',
	API = 'api',
	WEB = 'web',
}

/** Which remote files an SFTP pull picks up. */
export enum SftpSelectionStrategy {
	ALL_NEW = 'all-new',
	NEWEST_MATCHING = 'newest-matching',
	EXACT_NAME = 'exact-name',
	SINCE_LAST_SUCCESS = 'since-last-success',
}

/** What happens to a remote file after it was pulled. */
export enum PostRetrievalAction {
	LEAVE = 'leave',
	DELETE = 'delete',
	MOVE = 'move',
}

export enum ScheduleFrequency {
	DAILY = 'daily',
	WEEKLY = 'weekly',
	MONTHLY = 'monthly',
}

/** What the platform does when an expected file has not arrived (acted on by F2). */
export enum MissingFileAction {
	NONE = 'none',
	ALERT = 'alert',
}

/** Per-field violation codes carried in `details.fields[].code` of `FILE_CONFIG_INVALID`. */
export enum FileConfigFieldErrorCode {
	REQUIRED = 'REQUIRED',
	UNKNOWN_FIELD = 'UNKNOWN_FIELD',
	INVALID_TYPE = 'INVALID_TYPE',
	INVALID_FORMAT = 'INVALID_FORMAT',
	INVALID_VALUE = 'INVALID_VALUE',
	OUT_OF_RANGE = 'OUT_OF_RANGE',
	DUPLICATE = 'DUPLICATE',
	NOT_ALLOWED = 'NOT_ALLOWED',
	INCONSISTENT = 'INCONSISTENT',
}

/** One violation: a dotted/bracketed path into the body and its code. Never the value. */
export interface FileConfigFieldError {
	path: string
	code: FileConfigFieldErrorCode
}

export interface ImportDefinitionRef {
	key: string
	version: number
}

export interface PostRetrieval {
	action: PostRetrievalAction
	/** Required for `move`, `null` otherwise. */
	moveToDirectory: string | null
}

export interface SftpPullMethod {
	type: RetrievalMethodType.SFTP_PULL
	/** Reference to the tenant's connector (F4). Credentials live there, never here. */
	connectorId: string
	remoteDirectory: string
	filenamePattern: string
	selectionStrategy: SftpSelectionStrategy
	minimumAgeSeconds: number
	postRetrieval: PostRetrieval
}

export interface UploadLimits {
	maxFileSizeBytes: number
	maxFilesPerDay: number
}

export interface ApiUploadMethod {
	type: RetrievalMethodType.API
	filenamePattern: string
	limits: UploadLimits
}

export interface WebUploadMethod {
	type: RetrievalMethodType.WEB
	filenamePattern: string
	limits: UploadLimits
}

export type RetrievalMethod = SftpPullMethod | ApiUploadMethod | WebUploadMethod

export interface Retrieval {
	/** At most one method per type, in the order the admin listed them. */
	methods: RetrievalMethod[]
	/** `null` exactly when `methods` is empty. */
	defaultMethod: RetrievalMethodType | null
}

export interface FileSchedule {
	frequency: ScheduleFrequency
	/** ISO weekdays (1 = Monday). Non-empty for `weekly`, `[]` otherwise. */
	daysOfWeek: number[]
	/** 1–28 for `monthly`, `null` otherwise. */
	dayOfMonth: number | null
	/** IANA zone the times are read in. */
	timezone: string
	/** `HH:MM` — the file is not expected before this time. */
	after: string
	/** `HH:MM` — later than `after`; the file is due by this time. */
	expectedBefore: string
	missingGracePeriodMinutes: number
	missingAction: MissingFileAction
}

/** The four configuration fields every version stores. */
export interface FileConfigFields {
	enabled: boolean
	importDefinition: ImportDefinitionRef | null
	retrieval: Retrieval
	schedule: FileSchedule | null
}

/** `GET`/`PUT` response for one logical file. */
export interface FileConfigDTO extends FileConfigFields {
	fileType: LogicalFileType
	/** 0 while the tenant has no stored row (spec REQ-001). */
	version: number
	updatedAt: string | null
	updatedBy: string | null
}

export interface FileConfigListDTO {
	items: FileConfigDTO[]
}

/** One immutable entry of a file's history. */
export interface FileConfigVersionDTO extends FileConfigFields {
	version: number
	createdAt: string
	/** `null` for a seeded version. */
	createdBy: string | null
}

export interface FileConfigVersionListDTO {
	items: FileConfigVersionDTO[]
	nextBeforeVersion: number | null
}

/** `PUT` body: a full replacement guarded by the version the client last read. */
export interface FileConfigWriteRequest extends FileConfigFields {
	expectedVersion: number
}

/** The configuration a file has before anything was stored for it. */
export const DISABLED_FILE_CONFIG: Readonly<FileConfigFields> = Object.freeze({
	enabled: false,
	importDefinition: null,
	retrieval: { methods: [], defaultMethod: null },
	schedule: null,
})
